import api from './api';

export interface ImportStats {
  channels: number;
  videos: number;
}

export interface ImportResult {
  success: boolean;
  message: string;
  total_parsed: number;
  valid_records: number;
  inserted: number;
}

export interface OneStudioChannel {
  id: number;
  title: string | null;
  stream_url: string | null;
  format: string | null;
  timezone: string | null;
  company_id: number | null;
  channel_type: string | null;
  source: string | null;
  logo_ext: string | null;
  s3bucket: string | null;
  s3folder: string | null;
  created_at: string | null;
  updated_at: string | null;
  imported_at: string;
}

export interface OneStudioVideo {
  id: number;
  channel_id: number | null;
  title: string | null;
  description: string | null;
  duration: number;
  file_name: string | null;
  video_format: string | null;
  hd_width: number;
  hd_height: number;
  thumbnail_name: string | null;
  hls_url: string | null;
  s3bucket: string | null;
  s3folder: string | null;
  created_at: string | null;
  updated_at: string | null;
  imported_at: string;
}

// OTT-Studio uses the same schema as 1Studio
export interface OttStudioChannel extends OneStudioChannel {}
export interface OttStudioVideo extends OneStudioVideo {}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

export type SourcePlatform = '1studio' | 'ott-studio';

export const importService = {
  // Get import statistics (filtered by source platform)
  async getStats(source?: SourcePlatform): Promise<ImportStats> {
    const response = await api.get('/import/stats', {
      params: source ? { source } : {},
    });
    return response.data;
  },

  // Import channels from CSV (with source platform)
  async importChannels(file: File, source: SourcePlatform = '1studio'): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source', source);
    const response = await api.post('/import/channels', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5 minute timeout for large files
    });
    return response.data;
  },

  // Import videos from CSV (with source platform)
  async importVideos(file: File, source: SourcePlatform = '1studio'): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source', source);
    const response = await api.post('/import/videos', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 600000, // 10 minute timeout for very large files
    });
    return response.data;
  },

  // Get imported channels (filtered by source platform)
  async getChannels(limit = 100, offset = 0, source?: SourcePlatform): Promise<PaginatedResponse<OneStudioChannel>> {
    const response = await api.get('/import/channels', {
      params: { limit, offset, source },
    });
    return response.data;
  },

  // Get imported videos (filtered by source platform)
  async getVideos(limit = 100, offset = 0, channelId?: number, source?: SourcePlatform): Promise<PaginatedResponse<OneStudioVideo>> {
    const response = await api.get('/import/videos', {
      params: { limit, offset, channel_id: channelId, source },
    });
    return response.data;
  },

  // Clear imported data
  async clearData(type: 'channels' | 'videos' | 'all'): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/import/clear/${type}`);
    return response.data;
  },

  // OTT-Studio import methods (same API endpoints with source parameter)
  // Import OTT-Studio channels from CSV
  async importOttStudioChannels(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source', 'ottstudio');
    const response = await api.post('/import/channels', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000,
    });
    return response.data;
  },

  // Import OTT-Studio videos from CSV
  async importOttStudioVideos(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source', 'ottstudio');
    const response = await api.post('/import/videos', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 600000,
    });
    return response.data;
  },

  // Get OTT-Studio channels (uses same table, filtered by source if needed)
  async getOttStudioChannels(limit = 100, offset = 0): Promise<PaginatedResponse<OttStudioChannel>> {
    const response = await api.get('/import/channels', {
      params: { limit, offset, source: 'ottstudio' },
    });
    return response.data;
  },

  // Get OTT-Studio videos
  async getOttStudioVideos(limit = 100, offset = 0, channelId?: number): Promise<PaginatedResponse<OttStudioVideo>> {
    const response = await api.get('/import/videos', {
      params: { limit, offset, channel_id: channelId, source: 'ottstudio' },
    });
    return response.data;
  },
};

export default importService;

